import { useState, useMemo } from "react";
import { Eye, Edit, RotateCcw } from "lucide-react";
import styles from "./Refunds.module.css";
import SelectBox from "../../../../components/BaseComponents/SelectBox/SelectBox";
import RegisterRefundModal from "../../../../components/Finance/Payments/RegisterRefundModal";
import ViewRefundModal from "../../../../components/Finance/Payments/ViewRefundModal";


const TABS = [
  { key: "supplier", label: "Supplier Refunds" },
  { key: "client", label: "Client Refunds" },
];

const Refunds = () => {
  const [activeTab, setActiveTab] = useState("supplier");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);

  const [supplierRefunds] = useState([
    {
      id: 1,
      supplierName: "Hotelbeds",
      supplierEmail: "refunds@hotelbeds.com",
      bookingRef: "BK-2024-001234",
      componentType: "Hotel",
      amount: 320.00,
      currency: "EUR",
      date: "2024-03-10",
      reason: "Cancellation",
      status: "Completed",
      reference: "REF-SUP-20240310-001",
      notes: "Room cancellation refund received",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "Bank Transfer",
    },
    {
      id: 2,
      supplierName: "Airtuerk",
      supplierEmail: "finance@airtuerk.com",
      bookingRef: "BK-2024-001180",
      componentType: "Flight",
      amount: 185.00,
      currency: "EUR",
      date: "2024-03-08",
      reason: "Change",
      status: "Pending",
      reference: "REF-SUP-20240308-003",
      notes: "Route change fare difference",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "SEPA",
    },
    {
      id: 3,
      supplierName: "Diana Travel",
      supplierEmail: "accounts@dianatravel.com",
      bookingRef: "BK-2024-001150",
      componentType: "Hotel",
      amount: 540.00,
      currency: "EUR",
      date: "2024-03-06",
      reason: "Overpayment",
      status: "Completed",
      reference: "REF-SUP-20240306-002",
      notes: "Duplicate payment correction",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "Bank Transfer",
    },
    {
      id: 4,
      supplierName: "Allianz Travel",
      supplierEmail: "agency@allianz.com",
      bookingRef: "BK-2024-001120",
      componentType: "Insurance",
      amount: 95.00,
      currency: "EUR",
      date: "2024-03-04",
      reason: "Cancellation",
      status: "Failed",
      reference: "REF-SUP-20240304-001",
      notes: "Insurance cancellation pending review",
      account: "Credit Card Processing",
      accountId: 3,
      paymentMethod: "Visa",
    },
  ]);

  const [clientRefunds] = useState([
    {
      id: 101,
      customerName: "Sarah Johnson",
      customerEmail: "sarah.j@email.com",
      bookingRef: "BK-2024-001234",
      amount: 280.00,
      currency: "EUR",
      date: "2024-03-11",
      reason: "Booking Cancellation",
      status: "Completed",
      reference: "REF-CLI-20240311-001",
      notes: "Full booking cancellation refund",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "Bank Transfer",
    },
    {
      id: 102,
      customerName: "Michael Chen",
      customerEmail: "m.chen@company.com",
      bookingRef: "BK-2024-001200",
      amount: 150.00,
      currency: "EUR",
      date: "2024-03-09",
      reason: "Traveler Cancellation",
      status: "Pending",
      reference: "REF-CLI-20240309-002",
      notes: "One traveler removed from booking",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "SEPA",
    },
    {
      id: 103,
      customerName: "Emma Wilson",
      customerEmail: "emma.w@email.com",
      bookingRef: "BK-2024-001175",
      amount: 420.00,
      currency: "EUR",
      date: "2024-03-07",
      reason: "Credit Note Payout",
      status: "Completed",
      reference: "REF-CLI-20240307-003",
      notes: "Credit note issued and paid",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "Bank Transfer",
    },
    {
      id: 104,
      customerName: "James Brown",
      customerEmail: "j.brown@email.com",
      bookingRef: "BK-2024-001140",
      amount: 75.00,
      currency: "EUR",
      date: "2024-03-05",
      reason: "Booking Cancellation",
      status: "Failed",
      reference: "REF-CLI-20240305-001",
      notes: "Bank details incorrect",
      account: "Credit Card Processing",
      accountId: 3,
      paymentMethod: "Visa",
    },
  ]);

  const loading = false;
  const error = null;
  const saveLoading = false;

  const currentData = activeTab === "supplier" ? supplierRefunds : clientRefunds;

  const supplierReasonOptions = [
    { label: "All Reasons", value: "" },
    { label: "Cancellation", value: "Cancellation" },
    { label: "Change", value: "Change" },
    { label: "Overpayment", value: "Overpayment" },
  ];

  const clientReasonOptions = [
    { label: "All Reasons", value: "" },
    { label: "Booking Cancellation", value: "Booking Cancellation" },
    { label: "Traveler Cancellation", value: "Traveler Cancellation" },
    { label: "Credit Note Payout", value: "Credit Note Payout" },
  ];

  const reasonOptions = activeTab === "supplier" ? supplierReasonOptions : clientReasonOptions;

  const getStatusBadgeClass = (status) => {
    const baseClass = styles.statusBadge;
    switch (status) {
      case "Completed":
        return `${baseClass} ${styles.statusCompleted}`;
      case "Pending":
        return `${baseClass} ${styles.statusPending}`;
      case "Failed":
        return `${baseClass} ${styles.statusFailed}`;
      default:
        return baseClass;
    }
  };

  const getReasonBadgeClass = (reason) => {
    const baseClass = styles.reasonBadge;
    switch (reason) {
      case "Cancellation":
      case "Booking Cancellation":
        return `${baseClass} ${styles.reasonCancellation}`;
      case "Change":
      case "Traveler Cancellation":
        return `${baseClass} ${styles.reasonChange}`;
      case "Overpayment":
      case "Credit Note Payout":
        return `${baseClass} ${styles.reasonOverpayment}`;
      default:
        return baseClass;
    }
  };

  const filteredRows = useMemo(() => {
    return currentData.filter((row) => {
      const nameField = activeTab === "supplier" ? row.supplierName : row.customerName;
      const matchesSearch = search === "" ||
        row.bookingRef.toLowerCase().includes(search.toLowerCase()) ||
        nameField.toLowerCase().includes(search.toLowerCase()) ||
        row.reference.toLowerCase().includes(search.toLowerCase());

      const matchesStatus = statusFilter === "" || row.status === statusFilter;
      const matchesReason = reasonFilter === "" || row.reason === reasonFilter;
      const matchesDateFrom = dateFrom === "" || row.date >= dateFrom;
      const matchesDateTo = dateTo === "" || row.date <= dateTo;

      return matchesSearch && matchesStatus && matchesReason && matchesDateFrom && matchesDateTo;
    });
  }, [currentData, search, statusFilter, reasonFilter, dateFrom, dateTo, activeTab]);

  const completedAmount = filteredRows
    .filter(r => r.status === "Completed")
    .reduce((sum, r) => sum + r.amount, 0);
  const pendingAmount = filteredRows
    .filter(r => r.status === "Pending")
    .reduce((sum, r) => sum + r.amount, 0);
  const failedAmount = filteredRows
    .filter(r => r.status === "Failed")
    .reduce((sum, r) => sum + r.amount, 0);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearch("");
    setSearchInput("");
    setStatusFilter("");
    setReasonFilter("");
    setDateFrom("");
    setDateTo("");
    setSelectedRow(null);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleRegister = () => {
    setMode("add");
    setSelectedRow(null);
    setShowRegisterModal(true);
  };

  const handleEdit = (row) => {
    setMode("edit");
    setSelectedRow(row);
    setShowRegisterModal(true);
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setShowViewModal(true);
  };

  const handleSave = async (payload) => {
    try {
      if (mode === "add") {
        console.log(`Register ${activeTab} refund:`, payload);
      } else if (selectedRow?.id) {
        console.log(`Update ${activeTab} refund:`, selectedRow.id, payload);
      }
      setShowRegisterModal(false);
    } catch (err) {
      console.error(`Failed to save ${activeTab} refund:`, err);
    }
  };

  const searchPlaceholder = activeTab === "supplier"
    ? "Search by booking, supplier, or reference..."
    : "Search by booking, customer, or reference...";

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Refunds</div>
        <button className={styles.add} onClick={handleRegister}>
          + Register Refund
        </button>
      </div>

      <div className={styles.infoBar}>
        <span className={styles.infoIcon} aria-hidden>ℹ️</span>
        <span>
          {activeTab === "supplier"
            ? "Supplier refunds: money received back from suppliers due to cancellation, change, or overpayment."
            : "Client refunds: money returned to clients for booking cancellations, traveler removals, or credit note payouts."}
        </span>
      </div>

      <div className={styles.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => handleTabChange(tab.key)}
          >
            <RotateCcw size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Refunded</div>
          <div className={styles.summaryValue}>€{completedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className={styles.summaryCount}>{filteredRows.filter(r => r.status === "Completed").length} refunds</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Pending</div>
          <div className={styles.summaryValue} style={{ color: '#f59e0b' }}>€{pendingAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className={styles.summaryCount}>{filteredRows.filter(r => r.status === "Pending").length} refunds</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Failed</div>
          <div className={styles.summaryValue} style={{ color: '#ef4444' }}>€{failedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className={styles.summaryCount}>{filteredRows.filter(r => r.status === "Failed").length} refunds</div>
        </div>
      </div>

      <div className={styles.filterBar}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.addbtn}>
            Search
          </button>
        </form>

        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className={styles.dateInput}
          placeholder="From"
        />

        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className={styles.dateInput}
          placeholder="To"
        />

        <SelectBox
          placeholder="Reason: All"
          value={reasonFilter}
          options={reasonOptions}
          onChange={(e) => setReasonFilter(e.target.value)}
        />

        <SelectBox
          placeholder="Status: All"
          value={statusFilter}
          options={[
            { label: "All Status", value: "" },
            { label: "Completed", value: "Completed" },
            { label: "Pending", value: "Pending" },
            { label: "Failed", value: "Failed" },
          ]}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading refunds...</div>
      ) : (
        <>
          <div className={styles.meta}>
            Showing {filteredRows.length} of {currentData.length} refunds
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>BOOKING REF</th>
                  <th>{activeTab === "supplier" ? "SUPPLIER" : "CUSTOMER"}</th>
                  {activeTab === "supplier" && <th>COMPONENT</th>}
                  <th>REASON</th>
                  <th>AMOUNT</th>
                  <th>REFERENCE</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td className={styles.date}>{row.date}</td>
                    <td>
                      <span className={styles.bookingRef}>{row.bookingRef}</span>
                    </td>
                    <td>
                      {activeTab === "supplier" ? (
                        <div className={styles.entityInfo}>
                          <div className={styles.entityName}>{row.supplierName}</div>
                          <div className={styles.entityEmail}>{row.supplierEmail}</div>
                        </div>
                      ) : (
                        <div className={styles.entityInfo}>
                          <div className={styles.entityName}>{row.customerName}</div>
                          <div className={styles.entityEmail}>{row.customerEmail}</div>
                        </div>
                      )}
                    </td>
                    {activeTab === "supplier" && (
                      <td>
                        <span className={styles.badge}>{row.componentType}</span>
                      </td>
                    )}
                    <td>
                      <span className={getReasonBadgeClass(row.reason)}>
                        {row.reason}
                      </span>
                    </td>
                    <td className={styles.amount}>
                      €{row.amount.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    <td className={styles.reference}>{row.reference}</td>
                    <td>
                      <span className={getStatusBadgeClass(row.status)}>
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleView(row)}
                          title="View refund"
                        >
                          <Eye size={16} />
                        </button>
                        {row.status !== "Completed" && (
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleEdit(row)}
                            title="Edit refund"
                          >
                            <Edit size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <RegisterRefundModal
        open={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        mode={mode}
        initialData={selectedRow}
        onSave={handleSave}
        saveLoading={saveLoading}
        refundType={activeTab}
      />

      <ViewRefundModal
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        refund={selectedRow}
        refundType={activeTab}
      />
    </div>
  );
};

export default Refunds;